import * as React from 'react';
import { Button, DatePicker, Input, Popconfirm, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { HolidayRow } from '../types';
import { clampHolidayDateToPricingYearEnd } from '../utils';

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_DISPLAY_FORMAT = 'DD-MMM-YYYY';
const DATE_PICKER_FORMATS: string[] = [DATE_DISPLAY_FORMAT, DATE_FORMAT, 'DDMMYYYY'];

interface HolidayTableProps {
    rows: HolidayRow[];
    schoolNameMap: Map<string, string>;
    onRowChange: (clientId: string, updates: Partial<HolidayRow>) => void;
    onDelete: (row: HolidayRow) => void;
    pricingYearEndDate: string;
    disabled?: boolean;
    focusRowClientId?: string | null;
    onFocusDone?: () => void;
}

const HolidayTable: React.FC<HolidayTableProps> = ({
    rows,
    schoolNameMap,
    onRowChange,
    onDelete,
    pricingYearEndDate,
    disabled = false,
    focusRowClientId = null,
    onFocusDone,
}) => {
    const tableWrapperRef = React.useRef<HTMLDivElement>(null);
    const nameInputRef = React.useRef<any>(null);
    const didFocusRef = React.useRef(false);
    const [tableScrollY, setTableScrollY] = React.useState(400);

    React.useEffect(() => {
        const el = tableWrapperRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;

        const updateScrollY = () => {
            const header = el.querySelector(
                '.ant-table-header, thead.ant-table-thead, .ant-table-thead'
            ) as HTMLElement | null;
            const headerHeight = header?.getBoundingClientRect().height || 39;
            const next = Math.max(160, Math.floor(el.clientHeight - headerHeight));
            setTableScrollY((prev) => (prev === next ? prev : next));
        };

        const rafUpdate = () => requestAnimationFrame(updateScrollY);
        rafUpdate();
        const observer = new ResizeObserver(rafUpdate);
        observer.observe(el);
        window.addEventListener('resize', rafUpdate);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', rafUpdate);
        };
    }, [rows.length]);

    React.useEffect(() => {
        if (!focusRowClientId || !onFocusDone) return;
        didFocusRef.current = false;
    }, [focusRowClientId, onFocusDone]);

    React.useEffect(() => {
        if (!focusRowClientId || !onFocusDone || didFocusRef.current) return;
        const row = rows.find((r) => r.clientId === focusRowClientId);
        if (!row) return;
        if (tableWrapperRef.current) {
            const body = tableWrapperRef.current.querySelector(
                '.ant-table-body'
            ) as HTMLElement | null;
            const scroller = body ?? tableWrapperRef.current;
            scroller.scrollTop = scroller.scrollHeight;
        }
        const t = setTimeout(() => {
            if (nameInputRef.current?.focus) {
                nameInputRef.current.focus();
                didFocusRef.current = true;
                onFocusDone();
            }
        }, 100);
        return () => clearTimeout(t);
    }, [focusRowClientId, onFocusDone, rows]);

    const columns = React.useMemo<ColumnsType<HolidayRow>>(() => [
        {
            title: 'Holiday Name',
            dataIndex: 'name',
            key: 'name',
            width: '30%',
            render: (_value, record) => {
                const errorMessage = record.validationErrors?.name;
                if (record.status === 'new') {
                    return (
                        <div className="holiday-table__cell">
                            <Input
                                ref={record.clientId === focusRowClientId ? nameInputRef : undefined}
                                value={record.name}
                                disabled={disabled}
                                status={errorMessage ? 'error' : ''}
                                onChange={(event) => onRowChange(record.clientId, { name: event.target.value })}
                                placeholder="Holiday name"
                            />
                            {errorMessage ? (
                                <Typography.Text type="danger" className="holiday-table__error">
                                    {errorMessage}
                                </Typography.Text>
                            ) : null}
                        </div>
                    );
                }
                return <span>{record.name}</span>;
            },
        },
        {
            title: 'Date From',
            dataIndex: 'dateFrom',
            key: 'dateFrom',
            width: '18%',
            render: (_value, record) => {
                const errorMessage = record.validationErrors?.dateFrom;
                if (record.status === 'new') {
                    const value = record.dateFrom ? dayjs(record.dateFrom) : undefined;
                    const input = (
                        <div className="holiday-table__cell">
                            <DatePicker
                                value={value as Dayjs | undefined}
                                disabled={disabled}
                                status={errorMessage ? 'error' : ''}
                                format={DATE_PICKER_FORMATS}
                                onChange={(date) =>
                                    onRowChange(record.clientId, {
                                        dateFrom: clampHolidayDateToPricingYearEnd(date, pricingYearEndDate),
                                    })
                                }
                            />
                            {errorMessage ? (
                                <Typography.Text type="danger" className="holiday-table__error">
                                    {errorMessage}
                                </Typography.Text>
                            ) : null}
                        </div>
                    );
                    return errorMessage ? (
                        <Tooltip title={errorMessage}>{input}</Tooltip>
                    ) : (
                        input
                    );
                }
                return record.dateFrom ? dayjs(record.dateFrom).format(DATE_DISPLAY_FORMAT) : '';
            },
        },
        {
            title: 'Date To',
            dataIndex: 'dateTo',
            key: 'dateTo',
            width: '18%',
            render: (_value, record) => {
                const errorMessage = record.validationErrors?.dateTo;
                if (record.status === 'new') {
                    const value = record.dateTo ? dayjs(record.dateTo) : undefined;
                    const input = (
                        <div className="holiday-table__cell">
                            <DatePicker
                                value={value as Dayjs | undefined}
                                disabled={disabled}
                                status={errorMessage ? 'error' : ''}
                                format={DATE_PICKER_FORMATS}
                                onChange={(date) =>
                                    onRowChange(record.clientId, {
                                        dateTo: clampHolidayDateToPricingYearEnd(date, pricingYearEndDate),
                                    })
                                }
                            />
                            {errorMessage ? (
                                <Typography.Text type="danger" className="holiday-table__error">
                                    {errorMessage}
                                </Typography.Text>
                            ) : null}
                        </div>
                    );
                    return errorMessage ? (
                        <Tooltip title={errorMessage}>{input}</Tooltip>
                    ) : (
                        input
                    );
                }
                return record.dateTo ? dayjs(record.dateTo).format(DATE_DISPLAY_FORMAT) : '';
            },
        },
        {
            title: 'Schools Applied To',
            dataIndex: 'schoolIds',
            key: 'schoolIds',
            ellipsis: true,
            render: (_value, record) => {
                const names = record.schoolIds
                    .map((id) => schoolNameMap.get(id) ?? id)
                    .filter(Boolean)
                    .join(', ');
                return names || '—';
            },
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            render: (_value, record) => {
                const recordClientId = record?.clientId;
                const handleDelete = () => {
                    if (recordClientId && record) {
                        onDelete(record);
                    }
                };
                return (
                    <Popconfirm
                        title="Delete this holiday?"
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={handleDelete}
                        disabled={disabled}
                    >
                        <Button danger type="link" disabled={disabled}>
                            Delete
                        </Button>
                    </Popconfirm>
                );
            },
        },
    ], [disabled, focusRowClientId, onDelete, onRowChange, pricingYearEndDate, schoolNameMap]);

    return (
        <div ref={tableWrapperRef} className="holiday-table__wrapper">
        <Table
            rowKey={(record) => record.clientId}
            columns={columns}
            dataSource={rows}
            pagination={false}
            tableLayout="fixed"
            scroll={{ y: tableScrollY }}
            locale={{ emptyText: 'No holidays to display' }}
            className="holiday-table"
        />
        </div>
    );
};

export default HolidayTable;